<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Settings') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('admin_settings')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                        <div class="hidden sm:block">
                            <div class="align-middle inline-block min-w-full border-b border-ultralight">
                                <table class="table-listview table-listview--zebra">
                                    <thead>
                                    <tr>
                                        <th class="table-listview-l-th">{{ translateIfExists('Name') }}</th>
                                        <th class="table-listview-l-th">{{ translateIfExists('Description') }}</th>
                                    </tr>
                                    </thead>
                                    <tbody class="bg-white">
                                    <tr
                                        v-for="settingPage in settingPages"
                                        :key="settingPage.route"
                                        class="cursor-pointer"
                                        @click="$router.push(settingPage.route)"
                                    >
                                        <td class="table-listview-l-td whitespace-no-wrap">
                                            <div class="flex items-center">
                                                <div class="flex-shrink-0 rounded-full bg-gray-700 mr-3">
                                                    <svg-vue class="w-8 h-8 p-1.5 text-gray-200" :icon="settingPage.icon"></svg-vue>
                                                </div>
                                                <div class="text-sm font-medium text-gray-700">
                                                    {{ translateIfExists(settingPage.title) }}
                                                </div>
                                            </div>
                                        </td>
                                        <td class="table-listview-l-td">
                                            <div class="text-sm text-gray-500">{{ translateIfExists(settingPage.description) }}</div>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="sm:hidden p-4 space-y-3">
                            <router-link
                                v-for="settingPage in settingPages"
                                :key="'mobile-' + settingPage.route"
                                :to="settingPage.route"
                                class="block border border-bordergray-100 rounded p-3 bg-white"
                            >
                                <div class="flex items-center">
                                    <div class="flex-shrink-0 rounded-full bg-gray-700 mr-3">
                                        <svg-vue class="w-8 h-8 p-1.5 text-gray-200" :icon="settingPage.icon"></svg-vue>
                                    </div>
                                    <div class="min-w-0 flex-1">
                                        <div class="text-sm font-medium text-gray-700 truncate">
                                            {{ translateIfExists(settingPage.title) }}
                                        </div>
                                        <div class="text-xs text-gray-500 mt-1">
                                            {{ translateIfExists(settingPage.description) }}
                                        </div>
                                    </div>
                                </div>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    name: "index",
    methods: {
        translateIfExists(key) {
            const text = String(key || '').trim();
            if (text === '') {
                return '';
            }
            if (typeof this.$te === 'function' && this.$te(text)) {
                return this.$t(text);
            }
            return text;
        },
    },
    data() {
        return {
            settingPages: [
                {route: '/workspace/admin/settings/general', title: 'General', description: 'Configure general site settings', icon: 'font-awesome.keyboard-duotone'},
                {route: '/workspace/admin/settings/seo', title: 'SEO', description: 'Configure site SEO parameters', icon: 'font-awesome.search-duotone'},
                {route: '/workspace/admin/settings/dashboard', title: 'Dashboard', description: 'Configure dashboard notice texts by user group', icon: 'font-awesome.keyboard-duotone'},
                {route: '/workspace/admin/settings/appearance', title: 'Appearance', description: 'Configure branding assets for GUI, email, favicon and login background', icon: 'font-awesome.swatchbook-duotone'},
                {route: '/workspace/admin/settings/localization', title: 'Localization', description: 'Configure localization settings for the site', icon: 'font-awesome.language-duotone'},
                {route: '/workspace/admin/settings/authentication', title: 'Authentication', description: 'Configure registration, social login and related 3rd party integrations', icon: 'font-awesome.fingerprint-duotone'},
                {route: '/workspace/admin/settings/outgoing-mail', title: 'Outgoing mail', description: 'Configure outgoing email service preferences', icon: 'font-awesome.inbox-out-duotone'},
                {route: '/workspace/admin/settings/mail-templates', title: 'E-Mail templates', description: 'Central management for templates and system notifications including variables, preview and test email.', icon: 'font-awesome.keyboard-duotone'},
                {route: '/workspace/admin/settings/logging', title: 'Logging', description: 'Configure outgoing email service preferences', icon: 'font-awesome.exclamation-triangle-duotone'},
                {route: '/workspace/admin/settings/captcha', title: 'Captcha', description: 'Configure captcha settings & preferences', icon: 'font-awesome.robot-duotone'},
                {route: '/workspace/admin/settings/developer-options', title: 'Developer options', description: 'Configure technical settings for development, debugging and integrations.', icon: 'font-awesome.keyboard-duotone'},
                {route: '/workspace/admin/settings/shortcuts', title: 'Shortcuts', description: 'Configure leader-based keyboard shortcuts for the main menu navigation.', icon: 'font-awesome.keyboard-duotone'},
                {route: '/workspace/admin/settings/pwa', title: 'PWA settings', description: 'Configure progressive web app icons, colors and cache versioning', icon: 'font-awesome.cog-duotone'},
                {route: '/workspace/admin/settings/ai', title: 'AI settings', description: 'Configure global OpenAI connection, model defaults, budget and token limits.', icon: 'font-awesome.robot-duotone'},
                {route: '/workspace/admin/settings/logs', title: 'Logs', description: 'View and manage relevant system logs, including Laravel logs', icon: 'font-awesome.exclamation-triangle-duotone'},
                {route: '/workspace/admin/settings/orphaned-images', title: 'Orphaned files', description: 'Analyze and delete orphaned uploaded files from attachments.', icon: 'font-awesome.exclamation-triangle-duotone'},
                {route: '/workspace/admin/settings/translations', title: 'Translations', description: 'Manage available languages and translations', icon: 'font-awesome.language-regular'},
                {route: '/workspace/admin/settings/legal-information', title: 'Legal information', description: 'Manage legal navigation links for pages such as imprint, privacy policy and terms of use.', icon: 'regular.shield-keyhole'},
                {route: '/workspace/admin/settings/canned-replies', title: 'Text templates', description: 'Manage text templates for replies and default texts.', icon: 'font-awesome.keyboard-duotone'},
                {route: '/workspace/admin/settings/ticket-topics', title: 'Topic areas', description: 'Manage topic areas and internal visibility.', icon: 'font-awesome.fingerprint-duotone'},
                {route: '/workspace/admin/settings/labels', title: 'Labels', description: 'Manage labels for classification.', icon: 'font-awesome.search-duotone'},
                {route: '/workspace/admin/settings/process-steps', title: 'Process steps', description: 'Manage process steps and workflows.', icon: 'font-awesome.exclamation-triangle-duotone'},
                {route: '/workspace/admin/settings/service-categories', title: 'Service components', description: 'Manage service components and categories.', icon: 'font-awesome.swatchbook-duotone'},
                {route: '/workspace/admin/settings/statuses', title: 'Project statuses', description: 'Manage project status values.', icon: 'font-awesome.language-duotone'},
                {route: '/workspace/admin/settings/billings', title: 'Billing statuses', description: 'Manage billing status values.', icon: 'font-awesome.inbox-out-duotone'},
                {route: '/workspace/admin/settings/priorities', title: 'Priorities', description: 'Manage ticket priority levels', icon: 'font-awesome.robot-duotone'},
            ]
        };
    }
}
</script>
